/**
 * Wave prumo-simple · PrumoCard.
 *
 * One decision. One sentence. One button.
 *
 * Layout (top → bottom):
 *   1. Severity icon + kind label + project chip
 *   2. Human title (1-2 lines, max)
 *   3. ONE primary CTA  →  deep link (Link to deep route)
 *   4. Expand "Por que?" (PM: human explanation · Admin: technical JSON)
 *   5. Approve/Decline (only when status === 'proposed')
 *
 * Design rules (impeccable + emil-design):
 *   - No border-left stripe (impeccable ban)
 *   - Active scale(0.97) ease-out 160ms on every pressable
 *   - Tier / score / payload NEVER visible to PM — admin-gated only
 *   - Mobile-first: full-width card, 44px tap targets
 *   - No modals — decline expands inline note field
 *
 * Deep links: resolved via lib/prumo-deep-links.ts lookup table.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, type PrumoProposal } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { resolveDeepLink, resolveActionLabel, resolveWhyPhrase } from '@/lib/prumo-deep-links';

// ─── Severity icon ────────────────────────────────────────────────────────────

function SeverityIcon({ tier }: { tier: 1 | 2 | 3 }) {
  // Tier 1: info circle · Tier 2: warning triangle · Tier 3: danger diamond
  if (tier === 3) {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="shrink-0 text-error">
        <path d="M7 1L13 13H1L7 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <line x1="7" y1="5" x2="7" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="11" r="0.75" fill="currentColor" />
      </svg>
    );
  }
  if (tier === 2) {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="shrink-0 text-warn">
        <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="7" y1="4" x2="7" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="10" r="0.75" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="shrink-0 text-ink-muted">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="7" y1="5" x2="7" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="7" cy="10.5" r="0.75" fill="currentColor" />
    </svg>
  );
}

// ─── Kind label map (PM-visible) ──────────────────────────────────────────────

const KIND_LABEL_PT: Record<string, string> = {
  po_approve: 'Compra',
  rdo_draft: 'Diário de obra',
  medicao_draft: 'Medição',
  rfq_recommend: 'Cotação',
  anomaly_resolve: 'Ponto de atenção',
  budget_change: 'Orçamento',
  budget_overrun: 'Orçamento',
  supplier_followup: 'Cotação',
  fvs_overdue: 'Qualidade',
  rnc_unassigned: 'Não conformidade',
  doc_expiring: 'Documento',
  stage_slip: 'Cronograma',
  cost_variance: 'Financeiro',
  sla_breach: 'Daily logs',
  measurement_overdue: 'Medição',
  client_update: 'Cliente',
  quality_rejection: 'Qualidade',
  safety_alert: 'Segurança',
  procurement_pending: 'Compras',
};

const KIND_LABEL_EN: Record<string, string> = {
  po_approve: 'Purchase',
  rdo_draft: 'Daily log',
  medicao_draft: 'Measurement',
  rfq_recommend: 'Quote',
  anomaly_resolve: 'Attention item',
  budget_change: 'Budget',
  budget_overrun: 'Budget',
  supplier_followup: 'Quote',
  fvs_overdue: 'Quality',
  rnc_unassigned: 'Open item',
  doc_expiring: 'Document',
  stage_slip: 'Schedule',
  cost_variance: 'Financials',
  sla_breach: 'Daily logs',
  measurement_overdue: 'Measurement',
  client_update: 'Client',
  quality_rejection: 'Quality',
  safety_alert: 'Safety',
  procurement_pending: 'Procurement',
};

function kindLabel(kind: string, isEn: boolean): string {
  const map = isEn ? KIND_LABEL_EN : KIND_LABEL_PT;
  return map[kind] ?? (isEn ? 'Action' : 'Ação');
}

// ─── Outcome chip ─────────────────────────────────────────────────────────────

function OutcomeChip({ status, isEn }: { status: PrumoProposal['status']; isEn: boolean }) {
  const styles: Record<typeof status, string> = {
    executed: 'border-ok/40 bg-ok/8 text-ok',
    declined: 'border-error/40 bg-error/8 text-error',
    expired: 'border-line bg-surface-2 text-ink-subtle',
    approved: 'border-warn/40 bg-warn/8 text-warn',
    proposed: 'border-line bg-surface-2 text-ink-subtle',
  };
  const labels: Record<typeof status, string> = isEn
    ? {
        executed: 'Done',
        declined: 'Dismissed',
        expired: 'Expired',
        approved: 'In progress',
        proposed: 'Pending',
      }
    : {
        executed: 'Executado',
        declined: 'Recusado',
        expired: 'Expirado',
        approved: 'Em execução',
        proposed: 'Pendente',
      };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium',
        styles[status],
      )}
    >
      {labels[status]}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function PrumoCard({
  proposal,
  onDone,
}: {
  proposal: PrumoProposal;
  onDone?: () => void;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [expanded, setExpanded] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');

  const approve = useMutation({
    mutationFn: () => api.approvePrumoProposal(proposal.id),
    onSuccess: () => onDone?.(),
  });
  const decline = useMutation({
    mutationFn: (n: string | undefined) => api.declinePrumoProposal(proposal.id, n),
    onSuccess: () => {
      setDeclining(false);
      setNote('');
      onDone?.();
    },
  });

  const deepLink = resolveDeepLink(proposal);
  const actionLabel = resolveActionLabel(proposal);
  const whyPhrase = resolveWhyPhrase(proposal);
  const kLabel = kindLabel(proposal.kind, isEn);

  const mutationError: string | null =
    approve.error
      ? (approve.error as Error).message
      : decline.error
        ? (decline.error as Error).message
        : approve.data?.error ?? null;

  const isBusy = approve.isPending || decline.isPending;

  return (
    <article
      data-proposal-id={proposal.id}
      className={cn(
        'group rounded-lg border border-line bg-surface',
        'transition-[transform,opacity] duration-200 ease-out',
        'data-[busy=true]:opacity-60',
      )}
      data-busy={isBusy ? 'true' : undefined}
    >
      {/* ── Top strip: severity + kind + project ── */}
      <div className="flex items-center gap-2 border-b border-line/60 px-3 py-2">
        <SeverityIcon tier={proposal.tier} />
        <span className="text-[11px] font-medium text-ink-muted">
          {kLabel}
        </span>
        {proposal.projectName && (
          <>
            <span aria-hidden className="text-ink-subtle">·</span>
            <span className="min-w-0 truncate text-[11px] text-ink-subtle">
              {proposal.projectName}
            </span>
          </>
        )}
        {proposal.status !== 'proposed' && (
          <span className="ml-auto">
            <OutcomeChip status={proposal.status} isEn={isEn} />
          </span>
        )}
      </div>

      {/* ── Body: title + why phrase ── */}
      <div className="px-3 pb-2 pt-2.5">
        <h3 className="text-[14px] font-semibold leading-snug text-ink">
          {proposal.title}
        </h3>
        <p className="mt-0.5 text-[12px] leading-snug text-ink-muted">
          {whyPhrase}
        </p>
      </div>

      {/* ── Primary CTA: deep link ── */}
      <div className="px-3 pb-3">
        <Link
          to={deepLink}
          className={cn(
            'inline-flex w-full items-center justify-center rounded-md',
            'h-9 px-4 text-[13px] font-semibold',
            'bg-brand-500 text-white',
            // Emil-design: scale(0.97) on :active, ease-out 160ms
            'transition-transform duration-[160ms] ease-out active:scale-[0.97]',
            'focus-ring',
          )}
        >
          {actionLabel}
        </Link>
      </div>

      {/* ── "Por que?" expand ── */}
      <div className="border-t border-line/60">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={cn(
            'flex w-full items-center justify-between px-3 py-2',
            'text-[11px] font-medium text-ink-muted',
            'hover:bg-surface-2 transition-colors duration-100',
            'rounded-b-lg',
          )}
        >
          <span>{expanded ? (isEn ? 'Hide details' : 'Esconder detalhes') : (isEn ? 'Why this suggestion?' : 'Por que esta sugestão?')}</span>
          <span
            aria-hidden
            className={cn(
              'text-ink-subtle transition-transform duration-150 ease-out',
              expanded && 'rotate-180',
            )}
          >
            ▾
          </span>
        </button>

        {expanded && (
          <div
            className={cn(
              'px-3 pb-3 pt-1 text-[12px] leading-relaxed text-ink-muted',
              'border-t border-line/40',
            )}
          >
            {/* PM: human explanation */}
            <p className="text-[12px] leading-relaxed text-ink-muted">
              {proposal.subtitle
                ? proposal.subtitle
                : (isEn ? 'The system reviewed this project\'s data and identified that this action is needed.' : 'O sistema analisou os dados desta obra e identificou que esta ação é necessária.')}
            </p>

            {/* Sources as human-readable links — no kind/score leaked */}
            {proposal.sources.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {proposal.sources.filter((s) => s.url).map((s, i) => (
                  <li key={i}>
                    <a
                      href={s.url}
                      className="text-brand-500 underline-offset-2 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {/* Admin-only: technical accordion */}
            {isAdmin && (
              <details className="mt-3">
                <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-[0.08em] text-ink-subtle hover:text-ink">
                  {isEn ? 'Technical details' : 'Ver detalhes técnicos'}
                </summary>
                <div className="mt-1.5 rounded border border-line bg-surface-2 p-2">
                  <dl className="space-y-1 text-[11px]">
                    <div className="flex gap-2">
                      <dt className="text-ink-subtle">Tier</dt>
                      <dd className="tabular-nums text-ink">{proposal.tier}</dd>
                    </div>
                    {proposal.accuracyPct != null && (
                      <div className="flex gap-2">
                        <dt className="text-ink-subtle">{isEn ? 'Accuracy' : 'Acurácia'}</dt>
                        <dd className="tabular-nums text-ink">{proposal.accuracyPct}%</dd>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <dt className="text-ink-subtle">Kind</dt>
                      <dd className="font-mono text-ink">{proposal.kind}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-ink-subtle">ID</dt>
                      <dd className="truncate font-mono text-ink-muted">{proposal.id}</dd>
                    </div>
                  </dl>
                  {Object.keys(proposal.payload).length > 0 && (
                    <pre className="mt-2 max-h-32 overflow-auto rounded bg-surface-3/60 p-1.5 text-[10px] leading-snug text-ink-subtle">
                      {JSON.stringify(proposal.payload, null, 2)}
                    </pre>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* ── Approve/Decline row — only for pending proposals ── */}
      {proposal.status === 'proposed' && (
        <div className="border-t border-line/60 px-3 py-2.5">
          {declining ? (
            <div className="space-y-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={isEn ? 'Why did you dismiss this? (optional)' : 'Por que recusou? (opcional)'}
                className={cn(
                  'block w-full resize-none rounded border border-line bg-surface px-2 py-1.5',
                  'text-[12px] text-ink placeholder:text-ink-subtle',
                  'focus:border-brand-500 focus:outline-none',
                )}
                rows={2}
                maxLength={500}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => decline.mutate(note.trim() || undefined)}
                  disabled={isBusy}
                  className={cn(
                    'inline-flex h-9 flex-1 items-center justify-center rounded-md',
                    'border border-error/40 bg-error/5 text-[13px] font-semibold text-error',
                    'transition-transform duration-[160ms] ease-out active:scale-[0.97]',
                    'disabled:opacity-60',
                  )}
                >
                  {decline.isPending ? (isEn ? 'Dismissing…' : 'Recusando…') : (isEn ? 'Confirm dismissal' : 'Confirmar recusa')}
                </button>
                <button
                  type="button"
                  onClick={() => { setDeclining(false); setNote(''); }}
                  className={cn(
                    'inline-flex h-9 items-center justify-center rounded-md',
                    'border border-line bg-surface px-3 text-[13px] font-medium text-ink-muted',
                    'hover:bg-surface-2 transition-[transform,background-color] duration-[160ms] ease-out active:scale-[0.97]',
                  )}
                >
                  {isEn ? 'Cancel' : 'Cancelar'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => approve.mutate()}
                disabled={isBusy}
                className={cn(
                  'inline-flex h-9 flex-1 items-center justify-center rounded-md',
                  'bg-brand-500/10 text-[13px] font-semibold text-brand-600',
                  'border border-brand-500/30',
                  'transition-transform duration-[160ms] ease-out active:scale-[0.97]',
                  'disabled:opacity-60',
                )}
              >
                {approve.isPending ? (isEn ? 'Approving…' : 'Aprovando…') : (isEn ? 'Approve' : 'Aprovar')}
              </button>
              <button
                type="button"
                onClick={() => setDeclining(true)}
                disabled={isBusy}
                className={cn(
                  'inline-flex h-9 items-center justify-center rounded-md',
                  'border border-line bg-surface px-3 text-[13px] font-medium text-ink-muted',
                  'hover:bg-surface-2 transition-[transform,background-color] duration-[160ms] ease-out active:scale-[0.97]',
                  'disabled:opacity-60',
                )}
              >
                {isEn ? 'Dismiss' : 'Recusar'}
              </button>
            </div>
          )}
        </div>
      )}

      {mutationError && (
        <p className="px-3 pb-2 text-[11px] text-error" role="alert">
          {mutationError}
        </p>
      )}
    </article>
  );
}
